import { Box, Typography } from "@mui/material";
import { T } from "./tokens";

/** A dropdown option in the top bar. */
export interface FilterOption {
    value: string;
    label: string;
}

const TopBar = ({
    title,
    sub,
    showBack,
    onBack,
    team,
    teamOptions,
    onTeam,
    consultant,
    consultantOptions,
    onConsultant,
    weekLabel,
    onPrevWeek,
    onNextWeek,
    periodMode,
    onPeriodMode,
}: {
    title: string;
    sub: string;
    showBack?: boolean;
    onBack?: () => void;
    team?: string;
    teamOptions?: FilterOption[];
    onTeam?: (v: string) => void;
    consultant?: string;
    consultantOptions?: FilterOption[];
    onConsultant?: (v: string) => void;
    weekLabel: string;
    onPrevWeek: () => void;
    onNextWeek: () => void;
    periodMode?: "week" | "month";
    onPeriodMode?: (m: "week" | "month") => void;
}) => {
    const stepBtn = {
        border: `1px solid ${T.border}`,
        background: T.card,
        borderRadius: "7px",
        width: "30px",
        height: "30px",
        cursor: "pointer",
        color: T.ink2,
        fontSize: "15px",
        lineHeight: 1,
        "&:hover": { background: T.rowTint },
    } as const;

    return (
        <Box
            sx={{
                background: T.card,
                borderBottom: `1px solid ${T.border}`,
                padding: "12px 24px",
                display: "flex",
                alignItems: "center",
                gap: "16px",
                flexWrap: "wrap",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
                {showBack && (
                    <Box
                        component="button"
                        onClick={onBack}
                        sx={{
                            border: `1px solid ${T.border}`,
                            background: T.card,
                            borderRadius: "7px",
                            padding: "6px 10px",
                            cursor: "pointer",
                            fontSize: "13px",
                            color: T.ink2,
                            whiteSpace: "nowrap",
                            "&:hover": { background: T.rowTint },
                        }}
                    >
                        ← Back
                    </Box>
                )}
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: "19px", fontWeight: 800, color: T.ink2 }}>{title}</Typography>
                    <Typography sx={{ fontSize: "12.5px", color: T.muted }}>{sub}</Typography>
                </Box>
            </Box>

            <Box sx={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                {/* Team is the console's organising dimension. Region was
                    dropped: it is a six-value picklist on the employee record
                    that 33 of 84 consultants had set to "Other" or left blank,
                    and the business reports by team. */}
                {team && teamOptions && onTeam && (
                    <select
                        value={team}
                        onChange={(e) => onTeam(e.target.value)}
                        style={{
                            border: `1px solid ${T.border}`,
                            borderRadius: "8px",
                            padding: "7px 10px",
                            fontFamily: T.font,
                            fontSize: "13px",
                            color: T.ink2,
                            background: T.card,
                            cursor: "pointer",
                        }}
                    >
                        {teamOptions.map((t) => (
                            <option key={t.value} value={t.value}>
                                {t.label}
                            </option>
                        ))}
                    </select>
                )}

                {/* Consultant. Options narrow with the team, so the list stays
                    relevant rather than always being all 85. */}
                {consultant && consultantOptions && onConsultant && (
                    <select
                        value={consultant}
                        onChange={(e) => onConsultant(e.target.value)}
                        style={{
                            border: `1px solid ${T.border}`,
                            borderRadius: "8px",
                            padding: "7px 10px",
                            fontFamily: T.font,
                            fontSize: "13px",
                            color: T.ink2,
                            background: T.card,
                            cursor: "pointer",
                            maxWidth: "190px",
                        }}
                    >
                        {consultantOptions.map((c) => (
                            <option key={c.value} value={c.value}>
                                {c.label}
                            </option>
                        ))}
                    </select>
                )}

                {/* Week / Month toggle */}
                {periodMode && onPeriodMode && (
                    <Box sx={{ display: "flex", border: `1px solid ${T.border}`, borderRadius: "8px", overflow: "hidden" }}>
                        {(["week", "month"] as const).map((m) => (
                            <Box
                                key={m}
                                component="button"
                                onClick={() => onPeriodMode(m)}
                                sx={{
                                    border: "none",
                                    padding: "7px 12px",
                                    fontSize: "12px",
                                    fontWeight: 700,
                                    fontFamily: T.font,
                                    cursor: "pointer",
                                    background: periodMode === m ? T.ink : T.card,
                                    color: periodMode === m ? "#fff" : T.muted,
                                    "&:hover": { background: periodMode === m ? T.ink : T.rowTint },
                                }}
                            >
                                {m === "week" ? "Week" : "Month"}
                            </Box>
                        ))}
                    </Box>
                )}

                {/* Period stepper */}
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <Box component="button" onClick={onPrevWeek} sx={stepBtn}>‹</Box>
                    <Typography sx={{ minWidth: "128px", textAlign: "center", fontSize: "13px", fontWeight: 600, color: T.ink2 }}>
                        {weekLabel}
                    </Typography>
                    <Box component="button" onClick={onNextWeek} sx={stepBtn}>›</Box>
                </Box>
            </Box>
        </Box>
    );
};

export default TopBar;
